import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/date';
import {
  lookupBarcode, consumePartOnWorkOrder, getWorkOrderPartsUsed,
  getStores, getLocations, getInventoryParts, getInventoryPart,
  getRecentlyPurchasedParts,
} from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ScanLine, Package, Trash2, Plus, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { QrScanner } from './QrScanner';

/**
 * Parts-used panel for a work order. Allows scanning a barcode or picking a part,
 * choosing the source store/location, and auto-deducting stock.
 */
export const WorkOrderPartsPanel = ({ workOrder }) => {
  const { user } = useAuth();
  // Engineers must not see costs / totals — admin sets those after review.
  const isEngineer = user?.role === 'employee' || user?.role === 'subcontractor';
  const [used, setUsed] = useState(workOrder.partsUsed || []);
  const [scanOpen, setScanOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [part, setPart] = useState(null);
  const [stores, setStores] = useState([]);
  const [locationsByStore, setLocationsByStore] = useState({});
  const [storeId, setStoreId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [qty, setQty] = useState(1);
  // Manual part-picker state — search the inventory directory by name / SKU /
  // barcode, then click to drop into the confirm-quantity dialog. Same code
  // path as the barcode-scan flow downstream of `setPart`.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [allParts, setAllParts] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  // "Recently purchased" chip row — surfaces parts the team just received
  // into stock so common consumables (e.g. 13A plug top, junction box) are
  // one tap away on a fresh job. Reloaded each open so it always reflects
  // the latest stock-in movements.
  const [recentParts, setRecentParts] = useState([]);

  useEffect(() => {
    getStores().then(async (s) => {
      setStores(s);
      const map = {};
      await Promise.all(s.map(async (st) => { map[st.id] = await getLocations(st.id); }));
      setLocationsByStore(map);
    });
    getWorkOrderPartsUsed(workOrder.id).then(setUsed).catch(() => {});
  }, [workOrder.id]);

  const onScan = async (code) => {
    setScanOpen(false);
    try {
      const p = await lookupBarcode(code);
      // pick first location with stock as default
      const first = (p.stock || []).find(s => s.quantity > 0);
      setPart(p);
      setStoreId(first?.storeId || '');
      setLocationId(first?.locationId || '');
      setQty(1);
      setDialogOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No part for "${code}"`);
    }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerQuery('');
    // Fire both fetches in parallel — recents is small and quick, full list
    // is bigger but only fetched once per panel mount.
    const recentsPromise = getRecentlyPurchasedParts(8)
      .then(setRecentParts)
      .catch(() => setRecentParts([]));
    if (allParts.length === 0) {
      setPickerLoading(true);
      try {
        const list = await getInventoryParts();
        setAllParts(list || []);
      } catch (e) {
        toast.error('Failed to load parts');
      } finally {
        setPickerLoading(false);
      }
    }
    await recentsPromise;
  };

  const choosePart = async (p) => {
    setPickerOpen(false);
    // The list endpoint includes summary stock counts but we want the full
    // per-store/location stock array on `part`, so re-fetch the detail.
    try {
      const full = await getInventoryPart(p.id);
      const first = (full.stock || []).find(s => s.quantity > 0);
      setPart(full);
      setStoreId(first?.storeId || '');
      setLocationId(first?.locationId || '');
      setQty(1);
      setDialogOpen(true);
    } catch (e) {
      toast.error('Failed to load part details');
    }
  };

  const submit = async () => {
    if (!part || !storeId || !locationId) return toast.error('Pick source store & location');
    const q = Number(qty);
    if (!q || q <= 0) return toast.error('Quantity must be > 0');
    try {
      const usage = await consumePartOnWorkOrder(workOrder.id, {
        partId: part.id, storeId, locationId, qty: q, notes: '',
      });
      toast.success(`Consumed ${q} × ${part.name}`);
      setUsed(u => [...u, usage]);
      setDialogOpen(false);
      setPart(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to consume');
    }
  };

  const locationsFor = (sid) => locationsByStore[sid] || [];
  const billableTotalCost = used
    .filter(u => !u.nonBillable)
    .reduce((a, u) => a + (u.qty * (u.unitCost || 0)), 0);

  return (
    <div className="pt-4 border-t border-slate-200">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-slate-500">Parts used</div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={openPicker} data-testid="wo-pick-part-btn">
            <Plus className="h-4 w-4 mr-2" /> Pick part
          </Button>
          <Button size="sm" variant="outline" onClick={() => setScanOpen(true)} data-testid="wo-scan-part-btn">
            <ScanLine className="h-4 w-4 mr-2" /> Scan Barcode
          </Button>
        </div>
      </div>

      {used.length === 0
        ? <div className="text-sm text-slate-500">No parts consumed on this job yet.</div>
        : (
          <div className="space-y-1.5">
            {used.map((u, i) => (
              <div key={i} data-testid={`wo-part-used-${i}`} className="flex items-center gap-3 text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2">
                <Package className="h-4 w-4 text-slate-500" />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                    {u.qty} × {u.partName}
                    {u.nonBillable && (
                      <span
                        className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-900"
                        title="From a customer-owned store — not added to the draft invoice"
                        data-testid={`wo-part-nonbillable-${i}`}
                      >
                        Customer stock · not invoiced
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {u.sku}
                    {!isEngineer && !u.nonBillable && <> · £{((u.unitCost || 0) * u.qty).toFixed(2)}</>}
                    {' '}· {u.userName}
                  </div>
                </div>
              </div>
            ))}
            {!isEngineer && (
              <div className="text-right text-xs text-slate-600 pt-1">
                Total parts cost: <span className="font-semibold text-slate-900">£{billableTotalCost.toFixed(2)}</span>
              </div>
            )}
          </div>
        )
      }

      {/* Manual part picker — search by name / SKU / barcode */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-lg" data-testid="wo-part-picker-dialog">
          <DialogHeader><DialogTitle>Pick a part</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                autoFocus
                data-testid="wo-part-picker-search"
                placeholder="Search by name, SKU or barcode…"
                className="pl-9"
                value={pickerQuery}
                onChange={e => setPickerQuery(e.target.value)}
              />
            </div>

            {/* Recently purchased — shown only when no search is active so
                the chip strip doesn't fight the filtered list for space. */}
            {!pickerQuery.trim() && recentParts.length > 0 && (
              <div data-testid="wo-recent-parts-chips">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Recently purchased
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                  {recentParts.map(p => {
                    const totalStock = (p.stock || []).reduce((s, x) => s + (x.quantity || 0), 0);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => choosePart(p)}
                        data-testid={`wo-recent-chip-${p.id}`}
                        className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-full border border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                        title={`Last received ${formatDate(p.lastReceivedAt)} · +${p.lastReceivedQty}`}
                      >
                        <Package className="h-3 w-3" />
                        <span className="truncate max-w-[160px]">{p.name}</span>
                        <span className={`text-[10px] tabular-nums ${totalStock > 0 ? 'text-blue-700' : 'text-rose-600'}`}>
                          ·{totalStock}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="max-h-80 overflow-y-auto -mx-1">
              {pickerLoading && <div className="text-sm text-slate-500 p-4 text-center">Loading parts…</div>}
              {!pickerLoading && (() => {
                const q = pickerQuery.trim().toLowerCase();
                const filtered = q
                  ? allParts.filter(p =>
                      (p.name || '').toLowerCase().includes(q) ||
                      (p.sku || '').toLowerCase().includes(q) ||
                      (p.barcode || '').toLowerCase().includes(q))
                  : allParts;
                if (filtered.length === 0) {
                  return <div className="text-sm text-slate-400 italic p-4 text-center">No parts match.</div>;
                }
                return filtered.slice(0, 100).map(p => {
                  const totalStock = (p.stock || []).reduce((s, x) => s + (x.quantity || 0), 0);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`wo-part-picker-row-${p.id}`}
                      onClick={() => choosePart(p)}
                      className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50 rounded transition-colors"
                    >
                      <Package className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{p.name}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {p.sku ? <span className="font-mono">{p.sku}</span> : <span className="italic">no SKU</span>}
                          {p.barcode && <span> · {p.barcode}</span>}
                        </div>
                      </div>
                      <span className={`text-xs tabular-nums shrink-0 ${totalStock > 0 ? 'text-slate-700' : 'text-rose-600'}`}>
                        {totalStock} in stock
                      </span>
                    </button>
                  );
                });
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scanner */}
      <QrScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={onScan}
        title="Scan Part Barcode"
        hint="Scan the barcode or QR on the part being used."
      />

      {/* Confirm quantity */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record part used</DialogTitle></DialogHeader>
          {part && (
            <div className="space-y-3">
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="font-medium text-slate-900">{part.name}</div>
                <div className="text-xs text-slate-500">{part.sku} · barcode {part.barcode}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Source store</Label>
                  <Select value={storeId} onValueChange={(v) => { setStoreId(v); setLocationId(''); }}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select store" /></SelectTrigger>
                    <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Select value={locationId} onValueChange={setLocationId} disabled={!storeId}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      {locationsFor(storeId).map(l => {
                        const stock = part.stock?.find(s => s.storeId === storeId && s.locationId === l.id);
                        return <SelectItem key={l.id} value={l.id}>{l.name} ({stock?.quantity || 0} in stock)</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input type="number" min={1} value={qty} onChange={e => setQty(e.target.value)} className="mt-1.5" data-testid="wo-part-qty-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="wo-part-confirm-btn">Deduct stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
